/**
 * Search and filter logic for marketplace plugins
 * Provides fast, client-side filtering with debouncing support
 */

import type { MarketplaceEntry } from "../types/index";
import type { GitHubSource } from "@contracts/contract-1-sqs-envelope";

export interface SearchOptions {
  query: string;
  sortBy?: "name" | "updated" | "created";
  sortOrder?: "asc" | "desc";
}

/**
 * Search plugins by keyword across all major fields
 * Searches: pluginName, displayName, description, keywords, author, orgOrOwner, repoName
 * Case-insensitive, matches partial strings
 */
export function searchPlugins(plugins: MarketplaceEntry[], query: string): MarketplaceEntry[] {
  if (!query.trim()) {
    return plugins;
  }

  const lowerQuery = query.toLowerCase();

  return plugins.filter((plugin) => {
    if (plugin.pluginName.toLowerCase().includes(lowerQuery)) return true;
    if (plugin.displayName.toLowerCase().includes(lowerQuery)) return true;
    if (plugin.description.toLowerCase().includes(lowerQuery)) return true;
    if (plugin.keywords && plugin.keywords.some((kw: string) => kw.toLowerCase().includes(lowerQuery))) return true;
    if (plugin.author && plugin.author.toLowerCase().includes(lowerQuery)) return true;
    if (plugin.orgOrOwner.toLowerCase().includes(lowerQuery) || plugin.repoName.toLowerCase().includes(lowerQuery)) return true;
    return false;
  });
}

/**
 * Filter plugins by source
 */
export function filterBySource(plugins: MarketplaceEntry[], source: GitHubSource): MarketplaceEntry[] {
  return plugins.filter((p) => p.source === source);
}

/**
 * Filter plugins by skill type
 */
export function filterBySkillType(
  plugins: MarketplaceEntry[],
  skillType: "claude-code" | "cowork" | "copilot"
): MarketplaceEntry[] {
  return plugins.filter((p) => p.skillType === skillType);
}

/**
 * Sort plugins
 */
export function sortPlugins(
  plugins: MarketplaceEntry[],
  sortBy: "name" | "updated" | "created" = "name",
  sortOrder: "asc" | "desc" = "asc"
): MarketplaceEntry[] {
  const sorted = [...plugins];
  const multiplier = sortOrder === "asc" ? 1 : -1;

  sorted.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortBy) {
      case "name":
        aValue = a.pluginName.toLowerCase();
        bValue = b.pluginName.toLowerCase();
        return aValue < bValue ? -1 * multiplier : aValue > bValue ? 1 * multiplier : 0;

      case "updated":
        aValue = new Date(a.lastUpdatedAt).getTime();
        bValue = new Date(b.lastUpdatedAt).getTime();
        return (aValue - bValue) * multiplier;

      case "created":
        aValue = new Date(a.firstDiscoveredAt).getTime();
        bValue = new Date(b.firstDiscoveredAt).getTime();
        return (aValue - bValue) * multiplier;

      default:
        return 0;
    }
  });

  return sorted;
}

/**
 * Combined search and filter
 */
export function filterPlugins(plugins: MarketplaceEntry[], options: SearchOptions): MarketplaceEntry[] {
  let results = plugins;

  // Search by query
  if (options.query) {
    results = searchPlugins(results, options.query);
  }

  // Sort
  if (options.sortBy) {
    results = sortPlugins(results, options.sortBy, options.sortOrder);
  }

  return results;
}

/**
 * Debounce utility for search input
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delayMs: number = 300
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return function debounced(...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delayMs);
  };
}
